import React, {type ReactNode} from 'react'
import {ArrowPathIcon} from '@heroicons/react/24/outline'
import {type ChatConnectionSnapshot, requestChatConnection} from '@/Library/Chat/chatShell'

/** A notice centred in the chat's space: the connection's, and the chat page's empty and not-available states. */
export const chatNoticeClass = 'flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center text-sm text-storm-40'

/** The notices' one action, Try again. */
export const chatButtonClass = 'cursor-pointer rounded-lg border border-royal-10 bg-royal-10 px-3 py-2 text-sm font-semibold text-white hover:brightness-110 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-royal-20 focus-visible:ring-offset-2'

/**
 * What the docked panel and the chat page show in place of the chat while the connection is not live
 * (ENG-4739, ENG-4740): a spinner while connecting (and before the connection module has published), a sign-in
 * notice once the session ended, and Try again on an error. A plain function rather than a component, so callers' element-tree tests see its elements.
 */
export const chatConnectionNotice = ({status, organizationId}: Pick<ChatConnectionSnapshot, 'status' | 'organizationId'>): ReactNode => (
    <div data-testid="chat-status" className={chatNoticeClass}>
        {status === 'session-expired' ? (
            <p>Your session has ended. Sign in again to keep chatting.</p>
        ) : status === 'error' ? (
            <>
                <p>Chat could not connect.</p>
                <button
                    type="button"
                    data-testid="chat-retry"
                    className={chatButtonClass}
                    onClick={() => {
                        if (organizationId !== null) {
                            requestChatConnection(organizationId)
                        }
                    }}
                >
                    Try again
                </button>
            </>
        ) : (
            <span role="status" aria-label="Connecting to chat">
                <ArrowPathIcon className="size-6 animate-spin text-storm-40" aria-hidden="true"/>
            </span>
        )}
    </div>
)
